/**
 * Critical Error Dialog
 * 
 * A modal dialog that appears on top of everything when the app has a critical error state.
 * This ensures users understand the app may not work as expected.
 */

import { useEffect, useState } from 'react';
import { AlertTriangle, WifiOff, RefreshCw, X } from 'lucide-react';
import { useErrorStore } from '@/stores/errorStore';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export function CriticalErrorDialog() {
  const { errors, clearError } = useErrorStore();
  const { isOnline } = useNetworkStatus();
  const [dismissed, setDismissed] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  
  // Find the most critical error or offline state
  const criticalError = errors.find(e => e.severity === 'critical');
  const isOffline = !isOnline;
  
  // Create a unique key for the current error state
  const currentKey = isOffline ? 'offline' : criticalError?.id ?? null;
  
  // Reset dismissed state when error condition changes
  useEffect(() => {
    if (currentKey !== errorKey) {
      setErrorKey(currentKey);
      setDismissed(false);
    }
  }, [currentKey, errorKey]);
  
  // Determine what to show
  const shouldShow = (isOffline || criticalError) && !dismissed;
  
  // Don't render if nothing to show
  if (!shouldShow) return null;
  
  const handleDismiss = () => {
    setDismissed(true);
  };
  
  const handleRetry = () => {
    if (criticalError?.retryAction) {
      criticalError.retryAction();
      clearError(criticalError.id);
    } else {
      // Reload the page as last resort
      window.location.reload();
    }
  };
  
  const handleRefresh = () => {
    window.location.reload();
  };
  
  // Determine dialog content based on error type
  const isOfflineError = isOffline;
  const Icon = isOfflineError ? WifiOff : AlertTriangle;
  
  const title = isOfflineError 
    ? 'Ei verkkoyhteyttä' 
    : criticalError?.title || 'Virhetila';
    
  const description = isOfflineError
    ? 'Internet-yhteys puuttuu. Sovellus ei välttämättä toimi odotetusti.'
    : criticalError?.message || 'Sovelluksessa on tapahtunut virhe. Toiminnot eivät välttämättä toimi odotetusti.';
  
  const details = isOfflineError
    ? 'Tarkista verkkoyhteys ja yritä uudelleen. Tallentamattomat muutokset saattavat kadota.'
    : 'Voit yrittää päivittää sivun tai jatkaa rajoitetulla toiminnallisuudella. Tallentamattomat muutokset saattavat kadota.';
  
  return (
    <Dialog open={shouldShow} onOpenChange={(open) => !open && handleDismiss()}>
      <DialogContent 
        className="sm:max-w-md border-destructive/50 bg-background"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="space-y-4">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <Icon className="h-8 w-8 text-destructive" />
          </div>
          <DialogTitle className="text-center text-xl">
            {title}
          </DialogTitle>
          <DialogDescription className="text-center text-base">
            {description}
          </DialogDescription>
        </DialogHeader>
        
        <div className="rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground">
          <p>{details}</p>
          {criticalError?.code && (
            <p className="mt-2 font-mono text-xs opacity-70">
              Virhekoodi: {criticalError.code}
              {criticalError.traceId && ` (${criticalError.traceId.slice(0, 8)})`}
            </p>
          )}
        </div>
        
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button 
            onClick={handleRefresh}
            className="w-full gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Päivitä sivu
          </Button>
          
          <Button 
            variant="outline" 
            onClick={handleDismiss}
            className="w-full"
          >
            Jatka silti
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
